"use client";
import { Input } from "cy-fe/src/components/Forms/Input";
import { Button, FormError, FormGroup, Label } from "cy-fe";
import { PasswordInput } from "cy-fe/src/components/Forms/Input/PasswordInput";
import { useForm } from "react-hook-form";

export interface ResetPasswordFormInputs {
  otp: string;
  password: string;
  confirm: string;
}

export interface SubmitNewPasswordArgs {
  resetToken: string;
  password: string;
}
export function UpdateForm({
  onResendPin,
  onSubmit,
}: {
  onResendPin: () => void;
  onSubmit: (args: SubmitNewPasswordArgs) => Promise<number | undefined>;
}) {
  const form = useForm<ResetPasswordFormInputs>();
  const otpLength = 8;

  const allowResend = form.watch("otp", "").length !== otpLength;

  const handleSubmit = async (fields: ResetPasswordFormInputs) => {
    const responseCode = await onSubmit({
      resetToken: fields.otp,
      password: fields.password,
    });

    if (responseCode === 403) {
      form.setError("otp", { type: "manual", message: "Invalid code" });
    } else if (responseCode === 422) {
      form.setError("password", {
        type: "manual",
        message: "Invalid password",
      });
    }
  };

  return (
    <div className="text-default mx-auto w-[90dvw] max-w-[90dvw] space-y-6 px-2 sm:max-w-lg">
      <div className="mx-auto mt-5 mb-5 flex w-fit">
        <span className="mr-2 text-sm font-light">
          We have sent a one time code to your email or mobile number
        </span>
      </div>

      <form onSubmit={form.handleSubmit(handleSubmit)}>
        <FormGroup controller={{ control: form.control, name: "otp" }}>
          <Label className="text-sm font-light" htmlFor="otp">
            Enter your one time code
          </Label>
          <Input
            vSize="lg"
            id="otp"
            autoComplete="one-time-code"
            type="text"
            maxLength={otpLength}
            {...form.register("otp", {
              required: { value: true, message: "Required" },
              minLength: { value: otpLength, message: "Code must be 8 digits" }, // Remove this if we sort out the styling
            })}
          />
          <FormError />
        </FormGroup>

        {allowResend ? (
          <>
            <div className="mx-auto mt-5 mb-5 flex w-fit">
              <span className="mr-2 text-sm font-light">
                If you did not receive the code, you can request another
              </span>
            </div>
            <Button
              type="button"
              vColor="secondary"
              vSize="lg"
              className="w-full"
              onClick={onResendPin}
            >
              Resend Code
            </Button>
          </>
        ) : (
          <>
            <FormGroup controller={{ control: form.control, name: "password" }}>
              <Label className="mt-2 text-sm font-light" htmlFor="password">
                Enter a new password
              </Label>
              <PasswordInput
                inputProps={{
                  vSize: "lg",
                  id: "password",
                  autoComplete: "disabled",
                  ...form.register("password", {
                    required: { value: true, message: "Required" },
                    minLength: { value: 8, message: "Min Length 8" },
                    deps: ["confirm"],
                    pattern: {
                      value:
                        /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^a-zA-Z\d]).+$/,
                      message:
                        "Must have upper/lower case character, number, and a non alphanumeric character",
                    },
                  }),
                }}
              />
              <FormError />
            </FormGroup>
            <FormGroup controller={{ control: form.control, name: "confirm" }}>
              <Label className="mt-2 text-sm font-light" htmlFor="confirm">
                Re-enter your new password
              </Label>
              <PasswordInput
                inputProps={{
                  vSize: "lg",
                  id: "confirm",
                  autoComplete: "disabled",
                  ...form.register("confirm", {
                    validate: {
                      match: (val) => {
                        if (val !== form.watch("password", "")) {
                          return "Passwords dont match";
                        }
                      },
                    },
                  }),
                }}
              />
              <FormError />
            </FormGroup>

            <Button type="submit" vSize="lg" className="mt-5 w-full">
              Reset Password
            </Button>
          </>
        )}
      </form>
    </div>
  );
}
